import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Clock3, ExternalLink, Globe2,
  MapPin, Package, Plus, Send, ShoppingCart, Truck,
} from 'lucide-react';
import {
  ComposableMap, Geographies, Geography, Line, Marker, ZoomableGroup,
} from 'react-simple-maps';

import { useXomicsAuth } from './AuthContext';
import {
  dispatchCarrierShipment, loadShipment, loadShipments,
} from './api';
import { tx } from './i18n';
import { codeLabel, orderReferenceLabel } from './labels';
import {
  Empty, Failed, Head, Loading, Pill, RailHeader, SearchField, WorkspaceTabs,
} from './parts';
import {
  coordinateFor, curvedRoute, GEO_URL, mapView,
} from './routeMap';
import NewShipmentModal from './NewShipment';
import { ShipmentLabelButtons } from './ShippingLabelButton';
import { DispatchStock } from './StoreStock';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';

const PAGE_SIZE = 100;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const STATUS_TONE = {
  created: 'flat',
  label_printed: 'warn',
  dispatched: 'info',
  in_transit: 'info',
  delivered: 'ok',
  returned: 'warn',
  lost: 'bad',
  cancelled: 'flat',
};

function when(value) {
  if (value === null || value === undefined) return '—';
  return new Date(Number(value)).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function money(minor, currency) {
  if (minor === null || currency === null) return '—';
  return `${(Number(minor) / 100).toFixed(2)} ${currency}`;
}

function addressLine(location) {
  const address = location?.address;
  if (!address) return '—';
  return [address.city, address.region, address.countryCode].filter(Boolean).join(' · ') || '—';
}

function fullAddress(location) {
  const address = location?.address;
  if (!address) return [];
  return [
    address.line1,
    address.line2,
    [address.postalCode, address.city, address.region].filter(Boolean).join(' '),
    address.countryCode,
  ].filter(Boolean);
}

function subjectLabel(shipment) {
  if (shipment.subject.type === 'unlinked') return tx('ui.shipments.unlinked_shipment');
  if (shipment.subject.type === 'order') {
    return tx('ui.shipments.order_reference', { reference: orderReferenceLabel(shipment.subject.reference) });
  }
  if (shipment.subject.reference !== null) {
    return tx('ui.shipments.sample_reference', { reference: shipment.subject.printedReference ?? shipment.subject.reference });
  }
  return tx('ui.shipments.sample_shipment');
}

function routeFor(shipment) {
  const from = coordinateFor(shipment.fromLocation);
  const to = coordinateFor(shipment.destination);
  return {
    shipment,
    from,
    to,
    path: from !== null && to !== null ? curvedRoute(from, to) : null,
  };
}

function ShipmentMap({ shipment }) {
  const route = useMemo(() => (shipment === null ? null : routeFor(shipment)), [shipment]);
  const points = useMemo(() => {
    if (route === null || route.path === null) return [];
    return [route.from, route.to]
      .filter((coordinates) => coordinates !== null)
      .map((coordinates) => ({ coordinates }));
  }, [route]);
  const view = useMemo(() => mapView(points), [points]);
  const visualScale = Math.max(1, view.zoom);

  if (shipment === null || route === null) {
    return (
      <Empty
        title={tx('ui.shipments.no_shipments_match')}
        detail={tx('ui.shipments.adjust_filters')}
      />
    );
  }

  const dispatched = ['dispatched', 'in_transit', 'delivered', 'returned'].includes(shipment.status);
  const delivered = shipment.status === 'delivered';
  const pathStroke = delivered ? '#3e85f5' : dispatched ? '#789bd8' : '#aeb9ca';
  const destinationFill = delivered ? '#1d6bfb' : '#f7f9fd';
  const destinationText = delivered ? '#fff' : '#5578b6';

  return (
    <section className="xo-sample-map xo-shipment-map">
      <div className="xo-sample-map-heading">
        <span className="xo-sample-map-mark"><Truck aria-hidden="true" /></span>
        <div>
          <strong>{subjectLabel(shipment)}</strong>
          <span>{shipment.carrier.name} · {shipment.trackingNumber ?? tx('ui.samples.tracking_pending_5455cb5a')}</span>
        </div>
        <span className="xo-sample-map-count">
          {codeLabel('shipmentStatus', shipment.status)}
        </span>
      </div>

      <div className="xo-sample-map-canvas xo-shipment-map-canvas">
        <div className="xo-sample-map-viewport">
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ scale: 145 }}
            width={900}
            height={520}
            role="img"
            aria-label={tx('ui.shipments.shipment_details')}
          >
            <ZoomableGroup center={view.center} zoom={view.zoom} minZoom={1} maxZoom={24}>
              <Geographies geography={GEO_URL}>
                {({ geographies }) => geographies.map((geography) => (
                  <Geography
                    key={geography.rsmKey}
                    geography={geography}
                    fill="#e7ebf3"
                    stroke="#ffffff"
                    strokeWidth={0.7 / visualScale}
                    style={{
                      default: { outline: 'none' },
                      hover: { fill: '#dde3ed', outline: 'none' },
                      pressed: { outline: 'none' },
                    }}
                  />
                ))}
              </Geographies>

              {route.path !== null && (
                <Line
                  coordinates={route.path}
                  stroke={pathStroke}
                  strokeWidth={2.6 / visualScale}
                  strokeOpacity={dispatched ? 0.82 : 0.58}
                  strokeLinecap="round"
                  strokeDasharray={`${0.55 / visualScale} ${3.1 / visualScale}`}
                  fill="none"
                />
              )}

              {route.path !== null && route.from !== null && (
                <Marker coordinates={route.from}>
                  <circle r={10 / visualScale} fill="rgba(29,107,251,.12)"
                    stroke="#1d6bfb" strokeWidth={0.9 / visualScale} />
                  <circle r={6.5 / visualScale} fill="#1d6bfb" stroke="#fff"
                    strokeWidth={1.35 / visualScale} />
                  <text textAnchor="middle" y={2.3 / visualScale} fill="#fff"
                    fontSize={6.6 / visualScale} fontWeight="700">1</text>
                </Marker>
              )}

              {route.path !== null && route.to !== null && (
                <Marker coordinates={route.to}>
                  <circle r={10 / visualScale}
                    fill={delivered ? 'rgba(29,107,251,.12)' : 'rgba(126,148,184,.07)'}
                    stroke={delivered ? '#1d6bfb' : '#91a4c2'}
                    strokeDasharray={delivered ? undefined : `${1.4 / visualScale} ${1.8 / visualScale}`}
                    strokeWidth={0.9 / visualScale} />
                  <circle r={6.5 / visualScale} fill={destinationFill}
                    stroke={delivered ? '#fff' : '#9baac0'}
                    strokeWidth={1.35 / visualScale} />
                  <text textAnchor="middle" y={2.3 / visualScale} fill={destinationText}
                    fontSize={6.6 / visualScale} fontWeight="700">2</text>
                </Marker>
              )}
            </ZoomableGroup>
          </ComposableMap>
        </div>

        {route.path === null && (
          <div className="xo-shipment-abstract-route">
            <div className="xo-shipment-abstract-point is-origin">
              <span>1</span>
              <strong>{shipment.fromLocation.name}</strong>
              <small>{addressLine(shipment.fromLocation)}</small>
            </div>
            <span className="xo-shipment-abstract-line" aria-hidden="true">
              <MapPin />
            </span>
            <div className={`xo-shipment-abstract-point is-destination${delivered ? ' is-complete' : ''}`}>
              <span>2</span>
              <strong>{shipment.destination.name}</strong>
              <small>{addressLine(shipment.destination)}</small>
            </div>
            <p>{tx('ui.shipments.destination_not_geocoded')}</p>
          </div>
        )}
      </div>
    </section>
  );
}

function shipmentEvents(shipment) {
  const events = [{
    key: 'created',
    label: shipment.status === 'created'
      ? tx('ui.shipments.created') : codeLabel('shipmentStatus', 'label_printed'),
    at: shipment.createdAt ?? null,
  }];
  if (shipment.dispatchedAt !== null) {
    events.push({
      key: 'dispatched',
      label: tx('ui.orders.dispatched_ff07201a'),
      at: shipment.dispatchedAt,
    });
  }
  if (shipment.status === 'in_transit') {
    events.push({
      key: 'in_transit',
      label: codeLabel('shipmentStatus', 'in_transit'),
      at: shipment.updatedAt,
    });
  }
  if (shipment.deliveredAt !== null) {
    events.push({
      key: 'delivered',
      label: tx('ui.orders.delivered_eea956cd'),
      at: shipment.deliveredAt,
    });
  }
  if (['returned', 'lost', 'cancelled'].includes(shipment.status)) {
    events.push({
      key: shipment.status,
      label: codeLabel('shipmentStatus', shipment.status),
      at: shipment.updatedAt,
    });
  }
  return events.filter((event) => event.at !== null);
}

function nextShipmentEvent(shipment) {
  if (shipment.status === 'created') {
    return { key: 'next-label', label: codeLabel('shipmentStatus', 'label_printed') };
  }
  if (shipment.status === 'label_printed') {
    return { key: 'next-dispatch', label: codeLabel('shipmentStatus', 'dispatched') };
  }
  if (['dispatched', 'in_transit'].includes(shipment.status)) {
    return { key: 'next-delivery', label: codeLabel('shipmentStatus', 'delivered') };
  }
  return null;
}

function ShipmentTimeline({ shipment }) {
  const events = shipmentEvents(shipment);
  const next = nextShipmentEvent(shipment);
  return (
    <ol className="xo-shipment-timeline">
      {events.map((event) => (
        <li key={event.key}>
          <span><CheckCircle2 aria-hidden="true" /></span>
          <div><strong>{event.label}</strong><small>{when(event.at)}</small></div>
        </li>
      ))}
      {next !== null && (
        <li className="is-pending" key={next.key}>
          <span><Clock3 aria-hidden="true" /></span>
          <div><strong>{next.label}</strong><small>{tx('ui.samples.proposed_next_0066f8dc')}</small></div>
        </li>
      )}
    </ol>
  );
}

function ShipmentDetail({ uuid, locationScope, onChanged }) {
  const navigate = useNavigate();
  const { permissions, user } = useXomicsAuth();
  const mayManage = permissions.includes('SHIPMENT_MANAGE');
  const mayWriteSamples = permissions.includes('SAMPLE_TRACK_WRITE');
  const mayReadAllOrders = permissions.includes('ORDER_READ_ALL');
  const load = useCallback(() => loadShipment(uuid, { locationScope }), [locationScope, uuid]);
  const detail = useLiveResource(['shipments'], load);
  const [dispatching, setDispatching] = useState(false);
  const [dispatchError, setDispatchError] = useState('');
  // An order's shipment is asked first what it takes from the office's stock
  // (StoreStock.js); a sample's takes nothing and is dispatched at once.
  const [confirming, setConfirming] = useState(false);
  const ready = detail.data?.shipment?.uuid === uuid;

  async function dispatch() {
    setDispatching(true);
    setDispatchError('');
    try {
      await dispatchCarrierShipment(uuid);
      await detail.refresh();
      onChanged();
    } catch (requestError) {
      setDispatchError(errorMessage(requestError));
    } finally {
      setDispatching(false);
    }
  }

  if (!ready && !detail.error) {
    return (
      <aside className="xo-sample-map-panel xo-shipment-detail is-floating">
        <Loading label={tx('ui.shipments.loading_shipment')} />
      </aside>
    );
  }
  if (detail.error) {
    return (
      <aside className="xo-sample-map-panel xo-shipment-detail is-floating">
        <Failed message={detail.error} onRetry={detail.refresh} />
      </aside>
    );
  }

  const { shipment, items } = detail.data;
  const canDispatch = mayManage && shipment.fromLocation.id === user.location.id
    && shipment.subject.type !== 'unlinked'
    && shipment.status === 'label_printed'
    && (shipment.subject.type === 'order' || mayWriteSamples);
  const destinationAddress = fullAddress(shipment.destination);

  return (
    <aside className="xo-sample-map-panel xo-shipment-detail is-floating">
      <div className="xo-shipment-detail-scroll" onWheel={(event) => event.stopPropagation()}>
      <div className="xo-shipment-detail-head">
        <div>
          <p className="xo-eyebrow">{subjectLabel(shipment)}</p>
          <h3>{shipment.carrier.name}</h3>
          <p className="xo-shipment-tracking">
            {shipment.trackingNumber ?? tx('ui.samples.tracking_pending_5455cb5a')}
          </p>
        </div>
        <Pill tone={STATUS_TONE[shipment.status]}>{codeLabel('shipmentStatus', shipment.status)}</Pill>
      </div>

      <div className="xo-shipment-addresses">
        <div>
          <small>{tx('ui.samples.from_3f66052a')}</small>
          <strong>{shipment.fromLocation.name}</strong>
          {fullAddress(shipment.fromLocation).map((line) => <span key={line}>{line}</span>)}
        </div>
        <ArrowRight aria-hidden="true" />
        <div>
          <small>{tx('ui.samples.to_ae79ea1e')}</small>
          <strong>{shipment.destination.name}</strong>
          {destinationAddress.map((line) => <span key={line}>{line}</span>)}
        </div>
      </div>

      <dl className="xo-shipment-facts">
        <div><dt>{tx('ui.shipments.service')}</dt><dd>{shipment.serviceCode ?? '—'}</dd></div>
        {shipment.box && (
          <div><dt>{tx('ui.carrier.box')}</dt><dd>{shipment.box.name} · {shipment.box.lengthCm} × {shipment.box.widthCm} × {shipment.box.heightCm} cm</dd></div>
        )}
        <div><dt>{tx('ui.orders.items_44d25b5d')}</dt><dd>{shipment.items.units.toLocaleString()}</dd></div>
        <div><dt>{tx('ui.shipments.outbound_cost')}</dt><dd>{money(shipment.cost.outboundMinor, shipment.cost.currency)}</dd></div>
      </dl>

      <ShipmentTimeline shipment={shipment} />
      {shipment.subject.type === 'unlinked' && <p className="xo-alert t-warn">{tx('ui.shipments.collection_link_missing')}</p>}

      <div className="xo-shipment-items">
        <h4>{tx('ui.shipments.contents')}</h4>
        {items.map((item) => (
          <div key={item.id}>
            <Package aria-hidden="true" />
            <span>
              <strong>{item.product?.name ?? item.sample?.printedCode ?? item.sample?.code ?? tx('ui.shipments.shipment_item')}</strong>
              <small>{item.product?.code ?? item.sample?.printedCode ?? item.sample?.code ?? '—'}</small>
            </span>
            <strong>×{item.quantity}</strong>
          </div>
        ))}
      </div>

      <div className="xo-shipment-actions">
        {shipment.trackingUrl !== null && (
          <a className="xo-btn-2" href={shipment.trackingUrl} target="_blank" rel="noreferrer">
            <ExternalLink aria-hidden="true" /> {tx('ui.shipments.open_carrier_tracking')}
          </a>
        )}
        <ShipmentLabelButtons labels={shipment.labels} className="xo-shipment-label-action" />
        {canDispatch && (
          <button type="button" className="xo-btn" disabled={dispatching || confirming}
            onClick={shipment.subject.type === 'order' ? () => { setDispatchError(''); setConfirming(true); } : dispatch}>
            <Send aria-hidden="true" />
            {dispatching ? tx('ui.carrier.dispatching') : tx('ui.carrier.mark_dispatched')}
          </button>
        )}
        {shipment.subject.type === 'order' && shipment.subject.uuid !== null && (
          <button type="button" className="xo-btn-2"
            onClick={() => navigate(`/xomics/orders?orderUuid=${encodeURIComponent(shipment.subject.uuid)}${mayReadAllOrders ? '&scope=global' : ''}`)}>
            <ShoppingCart aria-hidden="true" /> {tx('ui.shipments.open_order')}
          </button>
        )}
        {shipment.subject.type === 'sample' && shipment.subject.uuid !== null && (
          <button type="button" className="xo-btn-2"
            onClick={() => navigate(`/xomics/sample-tracking?sampleUuid=${encodeURIComponent(shipment.subject.uuid)}`)}>
            <Package aria-hidden="true" /> {tx('ui.shipments.open_sample')}
          </button>
        )}
      </div>
      {shipment.note && <p className="xo-shipment-note">{shipment.note}</p>}
      {dispatchError && <p className="xo-alert t-bad" role="alert">{dispatchError}</p>}
      </div>
      {confirming && (
        <DispatchStock shipmentUuid={uuid} onClose={() => setConfirming(false)}
          onDispatched={() => { setConfirming(false); detail.refresh(); onChanged(); }} />
      )}
    </aside>
  );
}

function ShipmentCard({ shipment, selected, onSelect }) {
  return (
    <button
      type="button"
      className={`xo-shipment-card${selected ? ' is-selected' : ''}`}
      onClick={() => onSelect(shipment.uuid)}
    >
      <span className="xo-shipment-card-icon">
        {shipment.subject.type === 'order'
          ? <ShoppingCart aria-hidden="true" /> : <Package aria-hidden="true" />}
      </span>
      <span className="xo-shipment-card-copy">
        <span className="xo-shipment-card-top">
          <strong>{subjectLabel(shipment)}</strong>
          <Pill tone={STATUS_TONE[shipment.status]}>{codeLabel('shipmentStatus', shipment.status)}</Pill>
        </span>
        <small>{shipment.carrier.name} · {shipment.serviceCode ?? '—'}</small>
        <span className="xo-shipment-card-route">
          <span>{shipment.fromLocation.name}</span>
          <ArrowRight aria-hidden="true" />
          <span>{shipment.destination.name}</span>
        </span>
        <small>{addressLine(shipment.destination)} · {when(shipment.updatedAt)}</small>
      </span>
    </button>
  );
}

export default function XomicsShipments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions } = useXomicsAuth();
  const mayReadGlobal = permissions.includes('SHIPMENT_READ_ALL');
  const mayShip = permissions.includes('SHIPMENT_MANAGE');
  const [composing, setComposing] = useState(false);
  const [view, setView] = useState('transit');
  const [locationScope, setLocationScope] = useState(
    () => (mayReadGlobal && searchParams.get('scope') === 'global' ? 'global' : 'local'),
  );
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(null);
  const [pinnedUuid, setPinnedUuid] = useState(null);
  const linkedShipmentUuid = useMemo(() => {
    const value = String(searchParams.get('shipmentUuid') ?? '').trim();
    return UUID_RE.test(value) ? value : null;
  }, [searchParams]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setOffset(0);
  }, [view, debouncedQuery, locationScope]);

  useEffect(() => {
    if (!mayReadGlobal && locationScope !== 'local') setLocationScope('local');
  }, [locationScope, mayReadGlobal]);

  useEffect(() => {
    if (linkedShipmentUuid === null) return;
    setPinnedUuid(linkedShipmentUuid);
    setSelected(linkedShipmentUuid);
  }, [linkedShipmentUuid]);

  const scope = view === 'transit' ? 'active' : 'all';
  const status = view === 'delivered' ? 'delivered' : '';
  const serverQuery = debouncedQuery.length >= 3 ? debouncedQuery : '';
  const shortQuery = query.trim().length > 0 && query.trim().length < 3;

  const load = useCallback(() => loadShipments({
    scope, subject: 'all', status, query: serverQuery, limit: PAGE_SIZE, offset,
    locationScope,
  }), [locationScope, offset, scope, serverQuery, status]);
  const list = useLiveResource(['shipments'], load);
  const loadPinned = useCallback(
    () => loadShipment(pinnedUuid, { locationScope }),
    [locationScope, pinnedUuid],
  );
  const pinned = useLiveResource(['shipments'], loadPinned, { enabled: pinnedUuid !== null });
  const ready = list.data !== null
    && list.data.scope === scope
    && list.data.locationScope === locationScope
    && list.data.query === serverQuery
    && list.data.offset === offset;
  const shipments = useMemo(
    () => {
      if (!ready || shortQuery) return [];
      const seen = new Set();
      const listed = list.data.shipments.filter((shipment) => {
        if (seen.has(shipment.uuid)) return false;
        seen.add(shipment.uuid);
        return true;
      });
      const direct = pinned.data?.shipment ?? null;
      return direct !== null && !seen.has(direct.uuid) ? [direct, ...listed] : listed;
    },
    [list.data, pinned.data, ready, shortQuery],
  );
  const selectedShipment = useMemo(
    () => shipments.find((shipment) => shipment.uuid === selected)
      ?? (selected === null ? shipments[0] ?? null : null),
    [selected, shipments],
  );
  const summary = list.data?.summary ?? {
    total: 0, active: 0, completed: 0,
    byStatus: {}, bySubject: { order: 0, sample: 0 },
  };

  useEffect(() => {
    if (!ready) return;
    if (pinnedUuid !== null && selected === pinnedUuid) return;
    if (shipments.length === 0) {
      setSelected(null);
      return;
    }
    if (!shipments.some((shipment) => shipment.uuid === selected)) {
      setSelected(shipments[0].uuid);
    }
  }, [pinnedUuid, ready, selected, shipments]);

  function selectShipment(uuid) {
    if (uuid !== pinnedUuid) {
      setPinnedUuid(null);
      const next = new URLSearchParams(searchParams);
      next.delete('shipmentUuid');
      setSearchParams(next, { replace: true });
    }
    setSelected(uuid);
  }

  function changeQuery(value) {
    setPinnedUuid(null);
    const next = new URLSearchParams(searchParams);
    next.delete('shipmentUuid');
    setSearchParams(next, { replace: true });
    setQuery(value);
  }

  function changeView(next) {
    setView(next);
    setPinnedUuid(null);
    setSelected(null);
    const params = new URLSearchParams(searchParams);
    params.delete('shipmentUuid');
    setSearchParams(params, { replace: true });
  }

  function changeLocationScope(nextScope) {
    setLocationScope(nextScope);
    setPinnedUuid(null);
    setSelected(null);
    const params = new URLSearchParams(searchParams);
    params.delete('shipmentUuid');
    if (nextScope === 'global') params.set('scope', 'global');
    else params.delete('scope');
    setSearchParams(params, { replace: true });
  }

  const pageFrom = list.data?.total === 0 ? 0 : offset + 1;
  const pageTo = Math.min(offset + shipments.length, list.data?.total ?? 0);

  return (
    <>
      <div className="xo-shipment-board">
        <main className="xo-shipments-main">
          <Head
            title={tx('ui.nav.shipments_7a70be8a')}
            subtitle={tx('ui.shipments.shipment_details')}
          >
            {mayShip && (
              <button type="button" className="xo-btn" onClick={() => setComposing(true)}>
                <Plus aria-hidden="true" /> {tx('ui.shipments.new_shipment')}
              </button>
            )}
          </Head>
          {composing && (
            <NewShipmentModal
              onClose={() => setComposing(false)}
              onCreated={async (result) => {
                setComposing(false);
                await list.refresh();
                if (result?.shipment?.uuid) selectShipment(result.shipment.uuid);
              }}
            />
          )}

          <div className="xo-sample-map-shell xo-shipment-map-shell">
            {!ready && !list.error ? (
              <Loading label={tx('ui.shipments.loading_shipments')} />
            ) : list.error ? (
              <Failed message={list.error} onRetry={list.refresh} />
            ) : (
              <ShipmentMap shipment={selectedShipment} />
            )}
            {selected !== null && ready && (
              <ShipmentDetail
                uuid={selected}
                locationScope={locationScope}
                onChanged={list.refresh}
              />
            )}
          </div>
        </main>

        <aside className="xo-shipment-rail">
          <RailHeader title={tx('ui.nav.shipments_7a70be8a')} />
          <WorkspaceTabs className="xo-shipment-scope-tabs is-rail" value={view}
            label={tx('ui.shipments.shipment_scope')} onChange={changeView}
            tabs={[
              { value: 'transit', label: codeLabel('shipmentStatus', 'in_transit'), count: serverQuery === '' ? summary.active : undefined, Icon: Truck },
              { value: 'delivered', label: codeLabel('shipmentStatus', 'delivered'), count: serverQuery === '' ? summary.byStatus.delivered ?? 0 : undefined, Icon: CheckCircle2 },
            ]} />
          <div className="xo-workspace-search">
            <SearchField value={query} onChange={changeQuery}
              placeholder={tx('ui.shipments.search_shipments')} />
          </div>
          <WorkspaceTabs className="is-compact is-rail xo-shipment-location-tabs"
            value={locationScope} label={tx('ui.shipments.location_scope')}
            onChange={changeLocationScope}
            tabs={[
              { value: 'local', label: tx('ui.samples.local_dc99d54d'), Icon: MapPin },
              ...(mayReadGlobal ? [
                { value: 'global', label: tx('ui.samples.global_5f1184f7'), Icon: Globe2 },
              ] : []),
            ]} />
          <div className="xo-shipment-list" aria-busy={!ready}>
            {!ready && !list.error ? (
              <Loading label={tx('ui.shipments.loading_shipments')} />
            ) : list.error ? (
              <Failed message={list.error} onRetry={list.refresh} />
            ) : shipments.length === 0 ? (
              <Empty title={tx('ui.shipments.no_shipments_match')} detail={tx('ui.shipments.adjust_filters')} />
            ) : shipments.map((shipment) => (
              <ShipmentCard
                key={shipment.uuid}
                shipment={shipment}
                selected={selected === shipment.uuid}
                onSelect={selectShipment}
              />
            ))}
          </div>

          {ready && pinnedUuid === null && list.data.total > 0 && (
            <footer className="xo-shipment-pager">
              <span>{tx('ui.shipments.showing_range', {
                from: pageFrom, to: pageTo, total: list.data.total,
              })}</span>
              <div>
                <button type="button" className="xo-link" disabled={offset === 0 || list.loading}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                  {tx('ui.ui.previous_50f94286')}
                </button>
                <button type="button" className="xo-link"
                  disabled={(serverQuery !== '' ? !list.data.hasMore : pageTo >= list.data.total) || list.loading}
                  onClick={() => setOffset(offset + PAGE_SIZE)}>
                  {tx('ui.ui.next_bc981983')}
                </button>
              </div>
            </footer>
          )}
        </aside>
      </div>
    </>
  );
}
